<script setup lang="ts">
import { Animations, Direction, Sex } from "@/enums/characterGeneratorEnums";
import { SelectedItems } from "@/types/sheet-definiation";
import { ref } from "vue";
import CharacterCanvas from "./CharacterCanvas.vue";

export interface CharacterStagePreviewInstance {
  exportToGif: typeof exportToGif;
}

// eslint-disable-next-line
const props = withDefaults(
  defineProps<{
    selectedSex?: Sex;
    selectedItems: SelectedItems;
    selectedAnimation?: Animations;
  }>(),
  {
    selectedSex: Sex.Male,
    selectedAnimation: Animations.Walk,
  }
);

// 定义CharacterCanvas的类型
interface CharacterCanvasInstance {
  drawSpecificFrame: (frame: number) => HTMLCanvasElement | null;
  exportToGif: (options: {
    onComplete?: (blob: Blob, url: string) => void;
    autoDownload?: boolean;
    filename?: string;
  }) => void;
}

// 创建方向数组用于v-for循环
const directions = [
  Direction.Up,
  Direction.Left,
  Direction.Down,
  Direction.Right,
];

// 为各方向创建组件引用映射
const characterCanvasRefs = {
  [Direction.Up]: ref<CharacterCanvasInstance | null>(null),
  [Direction.Down]: ref<CharacterCanvasInstance | null>(null),
  [Direction.Left]: ref<CharacterCanvasInstance | null>(null),
  [Direction.Right]: ref<CharacterCanvasInstance | null>(null),
};

// 图片加载状态
const isAllImagesLoaded = ref(false);

// 当前放大的方向
const expandedDirection = ref<Direction | null>(null);

// 设置引用回调函数
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const setCanvasRef = (direction: Direction, el: any) => {
  if (el) {
    characterCanvasRefs[direction].value = el;
  }
};

// 导出GIF方法
const exportToGif = (
  direction: Direction = Direction.Down,
  options: {
    onComplete?: (blob: Blob, url: string) => void;
    autoDownload?: boolean;
  } = { autoDownload: false }
) => {
  const characterCanvas = characterCanvasRefs[direction].value;

  if (!characterCanvas) {
    console.warn("Canvas component not available");
    return;
  }

  // 调用组件的导出方法
  characterCanvas.exportToGif({
    onComplete: options.onComplete,
    autoDownload: options.autoDownload,
    filename: `character_animation_${Direction[direction]}.gif`,
  });
};

// 导出函数给父组件调用
defineExpose({
  exportToGif,
});

// 图片加载状态更新
const updateImageLoadedStatus = (status: boolean) => {
  isAllImagesLoaded.value = status;
};

// 处理Canvas点击事件
const handleCanvasClick = (direction: Direction) => {
  if (expandedDirection.value === direction) {
    // 如果点击的是已经展开的方向，则收起
    expandedDirection.value = null;
    animateCanvasLayout(false);
  } else {
    // 展开点击的方向
    expandedDirection.value = direction;
    animateCanvasLayout(true);
  }
};

// 使用GSAP动画处理布局变化
const animateCanvasLayout = (expand: boolean) => {
  const canvasElements = document.querySelectorAll(
    ".character-canvas-container"
  );

  canvasElements.forEach((el) => {
    const dirAttr = el.getAttribute("data-direction");
    const direction = dirAttr ? parseInt(dirAttr) : -1;

    // if (expand) {
    //   if (direction === expandedDirection.value) {
    //     // 展开选中的Canvas
    //     gsap.to(el, {
    //       duration: 0.5,
    //       scale: 1.2,
    //       opacity: 1,
    //       ease: "power2.out",
    //     });
    //   }
    //   // 其他Canvas的隐藏现在通过CSS类控制，不需要在这里设置
    // } else {
    //   // 恢复所有Canvas到原始状态
    //   gsap.to(el, {
    //     duration: 0.5,
    //     scale: 1,
    //     opacity: 1,
    //     ease: "power2.out",
    //   });
    // }
  });
};
</script>

<template>
  <div
    class="h-full w-full relative p-2"
    :class="{
      'grid grid-cols-2 gap-1': expandedDirection === null,
      'flex items-center justify-center': expandedDirection !== null,
    }"
  >
    <div
      v-for="direction in directions"
      :key="direction"
      class="character-canvas-container border rounded-lg bg-base-100 cursor-pointer transition-all flex items-center"
      :class="{
        expanded: expandedDirection === direction,
        hidden: expandedDirection !== null && expandedDirection !== direction,
      }"
      :data-direction="direction"
      @click="handleCanvasClick(direction)"
    >
      <CharacterCanvas
        class="w-full"
        :ref="(el) => setCanvasRef(direction, el)"
        :selectedSex="selectedSex"
        :selectedItems="selectedItems"
        :selectedAnimation="selectedAnimation"
        :direction="direction"
        @update:imageLoaded="updateImageLoadedStatus"
      />
    </div>
  </div>
</template>

<style scoped>
.character-canvas-container {
  transition: transform 0.5s ease, opacity 0.5s ease;
  transform-origin: center;
}

.expanded {
  z-index: 10;
  width: 80%;
  height: 80%;
}
</style>
